import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useProfileContext } from '../hooks/useProfileContext';
import { useWriteGate } from '../lib/writeGate';
import { setAppLanguage } from '../i18n';
import { toggleThemeMode } from './ThemeToggle';
import { apiGetShows, apiGetTeam, apiGetVenues } from '../api';
import type { ShowDTO, TeamMemberDTO, VenueDTO } from '../api';
import {
  NAV_COMMANDS,
  isFilterInert,
  modLabel,
  type PaletteNavContext,
} from '../lib/paletteCommands';

/**
 * P2b — the universal command palette (maquette 006 `.pal`): ⌘K / Ctrl+K.
 *
 * WHAT IT IS
 * - A modal overlay with three groups: Navigate (the static route index of
 *   `src/lib/paletteCommands.ts`, gated exactly like the sidebar), Actions
 *   (global, permission-gated actions that reuse the EXISTING APIs —
 *   `useWriteGate`, `setAppLanguage`, `toggleThemeMode`, `AuthContext.logout`)
 *   and People & places (live search over the existing endpoints).
 * - Presentation only: it changes NO route definition and NO business rule.
 *
 * KEYBOARD (the mockup's JS, ported)
 * - ⌘K / Ctrl+K toggles the palette FROM ANYWHERE, with `preventDefault` so the
 *   browser's own search bar never opens. While CLOSED this component handles
 *   no other key at all: typing in a form input is never stolen.
 * - Open: ↑/↓ move the selection (amber-soft row), ↵ runs it, Esc closes,
 *   ⌘0-9 / Ctrl+0-9 jump straight to the digit-mapped route.
 * - Click runs a row too.
 *
 * FILTER
 * - Instant, case-insensitive, on the TRANSLATED label + the row's keywords
 *   (the mockup's `data-t`). Empty groups are not rendered and the selection
 *   resets to the first row. `isFilterInert()` (localStorage, QA only) can
 *   disable it so the harness can prove it detects a broken filter.
 *
 * SEARCH (People & places)
 * - Debounced ~200 ms on `apiGetShows` / `apiGetTeam` / `apiGetVenues`
 *   (existing wrappers → `credentials: 'include'`), 5 hits per group, loading
 *   row while in flight, failures swallowed: `.catch(() => [])` — a search is
 *   not a save, there is no error banner here.
 * - ⚠️ The shows/team endpoints are BAND-SCOPED: no request is emitted at all
 *   when no band is active (early-return, the documented trap). `/api/venues`
 *   is a shared catalog, so it may run without a band.
 */
type GroupId = 'navigate' | 'actions' | 'entities';

interface PaletteRow {
  id: string;
  group: GroupId;
  icon: string;
  label: string;
  hint?: string;
  keywords: string;
  /** In-palette digit shortcut (Navigate rows only). */
  digit?: string;
  /** Route to run (navigate + close). */
  href?: string;
  /** In-place action (run + close). */
  run?: () => void;
}

interface RenderedGroup {
  id: GroupId;
  labelKey: string;
  entries: Array<{ row: PaletteRow; idx: number }>;
  loading?: boolean;
}

const EMPTY_HITS = { shows: [] as ShowDTO[], team: [] as TeamMemberDTO[], venues: [] as VenueDTO[] };

export interface CommandPaletteProps {
  /** Whether the palette is open (owned by AppLayout). */
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** The sidebar's visibility map — `pageVisibility()` computed in AppLayout. */
  vis: Record<string, boolean>;
}

export default function CommandPalette({ open, onOpen, onClose, vis }: CommandPaletteProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { activeProfileId, getProfile } = useProfileContext();
  const navigate = useNavigate();
  // SAME gate the « New show » button (ShowsPage) and the mobile FAB read.
  const writeGate = useWriteGate();
  // SAME capability the « + New Setlist » button on SetlistsPage reads.
  const canWriteSetlists = writeGate.canWriteCap('content.setlists');

  const [query, setQuery] = useState('');
  const [sel, setSel] = useState(0);
  const [hits, setHits] = useState(EMPTY_HITS);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const prevFocus = useRef<HTMLElement | null>(null);

  /* --------------------------- route context --------------------------- */
  const activeProfile = activeProfileId ? getProfile(activeProfileId) : null;
  // Mirrors AppLayout: slug first, id as fallback — never `/band//...`.
  const bandSlug = activeProfile?.slug || activeProfileId || '';
  const base = bandSlug ? `/band/${bandSlug}` : '';
  const ctx: PaletteNavContext = {
    base,
    band: !!base,
    vis,
    isAdmin: user?.global_role === 'superadmin' || !!user?.is_showcase,
  };
  const mod = modLabel();

  /* ------------------------- instant entity search --------------------- */
  useEffect(() => {
    if (!open) return;
    const term = query.trim().toLowerCase();
    if (!term) {
      setHits(EMPTY_HITS);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = window.setTimeout(async () => {
      // ⚠️ Band-scoped endpoints: WITHOUT an active band we emit NOTHING
      // (a `?band=` request with an empty id is a guaranteed 400).
      const band = ctx.band ? activeProfileId : null;
      const [shows, team, venues] = await Promise.all([
        band ? apiGetShows({ band }).catch(() => [] as ShowDTO[]) : Promise.resolve([] as ShowDTO[]),
        band ? apiGetTeam({ band }).catch(() => [] as TeamMemberDTO[]) : Promise.resolve([] as TeamMemberDTO[]),
        apiGetVenues().catch(() => [] as VenueDTO[]),
      ]);
      if (cancelled) return;
      const has = (hay: string) => hay.toLowerCase().includes(term);
      const head = (v: string | undefined | null) => (v || '').trim();
      setHits({
        shows: shows
          .filter((s) => has([s.name, s.venue_name, s.city, s.date].map(head).join(' ')))
          .slice(0, 5),
        team: team.filter((m) => has([m.name, m.role, m.nickname].map(head).join(' '))).slice(0, 5),
        venues: venues.filter((v) => has([v.name, v.city, v.country].map(head).join(' '))).slice(0, 5),
      });
      setSearching(false);
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // `ctx.band` derives from `activeProfileId` — listing it directly keeps the
    // effect honest without depending on a fresh object identity.
  }, [open, query, activeProfileId, ctx.band]);

  /* ---------------- open / close: focus + fresh query ------------------- */
  useEffect(() => {
    if (open) {
      prevFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setQuery('');
      setSel(0);
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    const back = prevFocus.current;
    prevFocus.current = null;
    if (back && document.contains(back)) back.focus();
  }, [open]);

  /* ------------------------------ rows --------------------------------- */
  const q = query.trim().toLowerCase();
  const inert = isFilterInert();
  const hit = (label: string, keywords: string) =>
    !q || inert || label.toLowerCase().includes(q) || keywords.toLowerCase().includes(q);

  const navRows: PaletteRow[] = NAV_COMMANDS.filter((c) => c.visible(ctx))
    .map((c) => ({
      id: c.id,
      group: 'navigate' as const,
      icon: c.icon,
      label: t(c.labelKey),
      hint: c.digit ? `${mod}${c.digit}` : undefined,
      digit: c.digit,
      keywords: c.keywords,
      href: c.route(ctx),
    }))
    .filter((r) => hit(r.label, r.keywords));

  const actionRows: PaletteRow[] = (
    [
      writeGate.canWrite && {
        id: 'action-new-show',
        group: 'actions',
        icon: '＋',
        label: t('palette.actions.newShow'),
        keywords: 'new show create add concert date',
        href: '/tour/shows/new',
      },
      // The create form lives on the Setlists page (there is no /setlists/new
      // route): offer the action only where its own button is enabled.
      canWriteSetlists && !!base && {
        id: 'action-new-setlist',
        group: 'actions',
        icon: '＋',
        label: t('palette.actions.newSetlist'),
        keywords: 'new setlist create songs music',
        href: `${base}/setlists`,
      },
      {
        id: 'action-account',
        group: 'actions',
        icon: '⚙',
        label: t('palette.actions.openAccount'),
        keywords: 'open account profile password sessions',
        href: '/account',
      },
      {
        id: 'action-theme',
        group: 'actions',
        icon: '◐',
        label: t('palette.actions.toggleTheme'),
        keywords: 'toggle theme dark light mode',
        run: () => toggleThemeMode(user?.id ?? null),
      },
      {
        id: 'action-lang-en',
        group: 'actions',
        icon: '文',
        hint: 'EN',
        label: t('palette.actions.switchLanguageTo', { lang: t('language.english') }),
        keywords: 'switch language english en idioma',
        run: () => setAppLanguage('en'),
      },
      {
        id: 'action-lang-fr',
        group: 'actions',
        icon: '文',
        hint: 'FR',
        label: t('palette.actions.switchLanguageTo', { lang: t('language.french') }),
        keywords: 'switch language french fr français langue',
        run: () => setAppLanguage('fr'),
      },
      {
        id: 'action-lang-es',
        group: 'actions',
        icon: '文',
        hint: 'ES',
        label: t('palette.actions.switchLanguageTo', { lang: t('language.spanish') }),
        keywords: 'switch language spanish es español idioma',
        run: () => setAppLanguage('es'),
      },
      {
        id: 'action-logout',
        group: 'actions',
        icon: '⏻',
        label: t('palette.actions.logout'),
        keywords: 'log out logout sign out leave',
        // Same two steps as `UserPanel.onLogout` in App.tsx.
        run: () => {
          void logout().then(() => navigate('/login', { replace: true }));
        },
      },
    ] as Array<PaletteRow | false>
  )
    .filter((r): r is PaletteRow => !!r)
    .filter((r) => hit(r.label, r.keywords));

  const entityRows: PaletteRow[] = [
    ...hits.shows.map((s) => {
      const primary = head2(s.name) || head2(s.venue_name) || head2(s.date) || String(s.id);
      const tail = [s.venue_name, s.city, s.date].map(head2).filter((v) => v && v !== primary);
      return {
        id: `entity-show-${s.id}`,
        group: 'entities' as const,
        icon: '◉',
        label: [primary, ...tail].join(' — '),
        keywords: 'show concert date',
        href: `/tour/shows/${s.id}`,
      };
    }),
    ...hits.team.map((m) => ({
      id: `entity-team-${m.id}`,
      group: 'entities' as const,
      icon: '@',
      label: [head2(m.name) || String(m.id), head2(m.role)].filter(Boolean).join(' — '),
      keywords: 'team person member',
      href: `/tour/team/${m.id}`,
    })),
    ...hits.venues.map((v) => ({
      id: `entity-venue-${v.id}`,
      group: 'entities' as const,
      icon: '⌂',
      label: [head2(v.name) || String(v.id), head2(v.city)].filter(Boolean).join(' — '),
      keywords: 'venue place hall city',
      href: `/tour/venues/${v.id}`,
    })),
  ];

  /* ------------------------- groups + selection ------------------------ */
  const flat: PaletteRow[] = [];
  const rendered: RenderedGroup[] = [];
  let cursor = 0;
  for (const g of [
    { id: 'navigate' as const, labelKey: 'palette.groups.navigate', rows: navRows },
    { id: 'actions' as const, labelKey: 'palette.groups.actions', rows: actionRows },
    { id: 'entities' as const, labelKey: 'palette.groups.entities', rows: entityRows },
  ]) {
    const entries = g.rows.map((row) => {
      const idx = cursor++;
      flat.push(row);
      return { row, idx };
    });
    const loading = g.id === 'entities' && searching && !!q && entries.length === 0;
    if (!entries.length && !loading) continue;
    rendered.push({ id: g.id, labelKey: g.labelKey, entries, loading });
  }
  const selIdx = flat.length ? Math.min(sel, flat.length - 1) : 0;

  // The filter (or a search result) changed → start back on the first row.
  useEffect(() => {
    setSel(0);
  }, [query, hits, open]);

  const runRow = (row: PaletteRow) => {
    if (row.run) row.run();
    else if (row.href) navigate(row.href);
    onClose();
  };

  // Keep the selected row inside the scroller without moving the page.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector('[data-palette-selected="true"]') as HTMLElement | null;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [selIdx, open, rendered.length]);

  /* ----------------------------- shortcuts ----------------------------- */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const modDown = e.metaKey || e.ctrlKey;
      if (modDown && e.key.toLowerCase() === 'k') {
        // Never let the browser's own quick-search (Firefox/Chrome) fire.
        e.preventDefault();
        if (open) onClose();
        else onOpen();
        return;
      }
      // CLOSED → this component captures nothing else (no stolen keystrokes).
      if (!open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSel((s) => (flat.length ? Math.min(s + 1, flat.length - 1) : 0));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSel((s) => Math.max(s - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const row = flat[selIdx];
        if (row) runRow(row);
        return;
      }
      if (modDown && /^[0-9]$/.test(e.key)) {
        const row = navRows.find((r) => r.digit === e.key);
        if (row) {
          e.preventDefault();
          runRow(row);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  /* Signed-out surfaces (login, public share links) get no palette. */
  if (!user) return null;
  if (!open) return null;

  return (
    <div
      className="pal open"
      data-palette-root
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="pal-box"
        role="dialog"
        aria-modal="true"
        aria-label={t('palette.title')}
        data-palette-box
      >
        <div className="pal-in">
          <span className="pal-caret" aria-hidden="true">›</span>
          <input
            ref={inputRef}
            data-palette-input
            className="pal-q"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('palette.placeholder')}
            aria-label={t('palette.title')}
            autoComplete="off"
            spellCheck={false}
          />
          <span className="pal-esc" aria-hidden="true">{t('palette.esc')}</span>
        </div>

        <div className="pal-list" ref={listRef} data-palette-list>
          {rendered.map((g) => (
            <div key={g.id} className="pal-group" data-palette-group={g.id}>
              <div className="pal-g">{t(g.labelKey)}</div>
              {g.entries.map(({ row, idx }) => (
                <button
                  key={row.id}
                  type="button"
                  className={`pal-i${idx === selIdx ? ' sel' : ''}`}
                  data-palette-item
                  data-palette-id={row.id}
                  data-palette-href={row.href}
                  data-palette-selected={idx === selIdx ? 'true' : undefined}
                  onClick={() => runRow(row)}
                  onMouseEnter={() => setSel(idx)}
                >
                  <span className="ic" aria-hidden="true">{row.icon}</span>
                  <span className="pal-t">{row.label}</span>
                  {row.hint && <span className="hint">{row.hint}</span>}
                </button>
              ))}
              {g.loading && (
                <div className="pal-loading" data-palette-loading>
                  {t('palette.loading')}
                </div>
              )}
            </div>
          ))}
          {!flat.length && !searching && (
            <div className="pal-empty" data-palette-empty>
              {t('palette.noResults')}
            </div>
          )}
        </div>

        <div className="pal-f">
          <span>{t('palette.footer.navigate')}</span>
          <span>{t('palette.footer.open')}</span>
          <span>{t('palette.footer.close')}</span>
          <span className="pal-f-brand">{t('app.name')} {mod}K</span>
        </div>
      </div>
    </div>
  );
}

/** Trim an optional field for label building (entity rows are DATA, not UI text). */
function head2(v: string | undefined | null): string {
  return (v || '').trim();
}

/**
 * P2b — the visible entry point: the bottom-of-sidebar search button
 * (« 🔍 Search ⌘K »). Mobile has no keyboard shortcut, so this button is the
 * only entry there (the More drawer renders the same sidebar, same button).
 */
export function PaletteOpenButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="nav-link sidebar-search" data-palette-open onClick={onClick}>
      <span className="sidebar-search-ic" aria-hidden="true">🔍</span>
      <span className="sidebar-search-label">{t('palette.open')}</span>
      <kbd className="sidebar-search-kbd">{modLabel()}K</kbd>
    </button>
  );
}
